import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { validate } from 'validate.js';

import {
  Alert,
  Box,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  List,
  Modal,
  Paragraph,
  Text,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_updateFeedImportedFees } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { removeErrorKey } from '@zg-rentals/zillow-rental-manager/utils';

import { useGetListingDetails } from '../../../hooks/queries/properties';

import { emitClickSaveFeeEvent, EVENT_IDS } from '../../../analytics/events/multiFamilyCostsAndFees';
import costsAndFeesConstraints from '../../../validation/costsandFeesConstraints';
import { useAuth } from '../../AuthProvider';
import ModalFooter from '../ModalFooter';

import type {
  GetMultifamilyFeesDictionaryItemsDto,
  MultifamilyFeeDto,
  UpdateFeedImportedMultifamilyFeesRequestDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  onClose: ({ isSaving }: { isSaving: boolean }) => void;
  feeToEdit: MultifamilyFeeDto;
  dictionaryItems?: GetMultifamilyFeesDictionaryItemsDto;
};

const MultifamilyFeedImportedFeeModal = ({ onClose, feeToEdit, dictionaryItems }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { enqueueToast } = useToast();

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  // Initialize feeDisplayName based on isCustomFeedFeeName flag
  const initialFeeDisplayName = (feeToEdit?.isCustomFeedFeeName && feeToEdit?.feeDisplayName) || '';
  const initialFeeDisplayDescription = feeToEdit?.feeDisplayDescription || '';

  const [feeDisplayName, setFeeDisplayName] = useState(initialFeeDisplayName);
  const [feeDisplayDescription, setFeeDisplayDescription] = useState(initialFeeDisplayDescription);
  const [apiError, setApiError] = useState<React.ReactNode | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Get original fee name - use feeName if available, otherwise fallback to dictionary display name
  const originalFeeName =
    feeToEdit?.feeName ||
    (dictionaryItems && feeToEdit?.feeCategory && feeToEdit?.feeType
      ? dictionaryItems.feeCategory[feeToEdit.feeCategory]?.feeType?.[feeToEdit.feeType]?.displayName
      : null);

  const { mutate: updateFee, isPending: isUpdatingFee } =
    useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_updateFeedImportedFees({
      onSuccess: () => {
        onClose({ isSaving: true });

        enqueueToast(<Toast tone="success">Fee updated</Toast>);
      },
      onError: (err: unknown) => {
        logError({
          error: err,
          context: {
            message: `Failed to update multifamily fee for listingAlias: ${listingAlias}, feeGroupId: ${feeToEdit?.feeGroupId}`,
            details: 'Update multifamily fee',
          },
        });

        setApiError(
          <>
            <Paragraph>Your changes couldn’t be saved. Try again. Error details:</Paragraph>
            <List.Root>
              <List.Item>{DatarouterError.getDisplayMessage(err)}</List.Item>
            </List.Root>
          </>,
        );
      },
      meta: {
        invalidates: ['mf-costsAndFees', 'listingRecommendations'],
        context: { api: 'rental-manager', details: 'update feed-imported multifamily fee' },
      },
    });

  const validateField = (fieldName: string, fieldValue: string | number) => {
    let error = '';

    if (fieldName === 'customFeeName') {
      setFieldErrors({
        ...fieldErrors,
        [fieldName]: '',
      });
      return;
    }

    const constraintsForField = costsAndFeesConstraints.values?.[fieldName];
    if (constraintsForField) {
      const validationError = validate({ [fieldName]: fieldValue }, { [fieldName]: constraintsForField })?.[
        fieldName
      ]?.[0];
      if (validationError) {
        error = removeErrorKey(fieldName, validationError);
      }
    }

    setFieldErrors({
      ...fieldErrors,
      [fieldName]: error,
    });
  };

  const canSubmit = useMemo<boolean>(() => {
    if (Object.values(fieldErrors).some((error) => error !== '')) {
      return false;
    }

    const hasNameChanged = feeDisplayName !== initialFeeDisplayName;
    const hasDescriptionChanged = feeDisplayDescription !== initialFeeDisplayDescription;
    const hasAnyChanges = hasNameChanged || hasDescriptionChanged;

    return hasAnyChanges;
  }, [fieldErrors, feeDisplayName, feeDisplayDescription, initialFeeDisplayName, initialFeeDisplayDescription]);

  const handleSaveClick = () => {
    if (listingDetails && user) {
      emitClickSaveFeeEvent({ currentProperty: listingDetails, user });
    }

    const postData: UpdateFeedImportedMultifamilyFeesRequestDto = {
      feeGroupId: feeToEdit?.feeGroupId,
      feeAmountMin: feeToEdit?.feeAmountMin,
      feeAmountMax: feeToEdit?.feeAmountMax,
      feeApplicableLevelType: feeToEdit?.feeApplicableLevelType,
      feeCalculationType: feeToEdit?.feeCalculationType,
      feeCategory: feeToEdit?.feeCategory,
      feeDescription: feeToEdit?.feeDescription,
      feeType: feeToEdit?.feeType,
      feePaymentFrequencyType: feeToEdit?.feePaymentFrequencyType,
      feeRefundableType: feeToEdit?.feeRefundableType,
      feeRequirementType: feeToEdit?.feeRequirementType,
      listingAlias,
      toUpdateFields: {
        feeDisplayName: feeDisplayName || '', // Send empty string to clear custom name
        feeDisplayDescription: feeDisplayDescription || '', // Send empty string to clear description
      },
      feeCreatedMs: feeToEdit?.feeCreatedMs,
      feePaymentFrequencyCategoryType: feeToEdit?.feePaymentFrequencyCategoryType,
      floorplan: feeToEdit?.floorplan,
      unit: feeToEdit?.unit,
    };

    if (feeToEdit.feeName) {
      postData.feeName = feeToEdit.feeName;
    }

    if (feeToEdit.feeRefundableType) {
      postData.feeRefundableType = feeToEdit.feeRefundableType;
    }

    updateFee({ postData });
  };

  const handleFeeDisplayNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFeeDisplayName(value);
    validateField('customFeeName', value);
  };

  const handleFeeDisplayDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFeeDisplayDescription(value);
    validateField('feeDescription', value);
  };

  const renderFooter = () => {
    return (
      <ModalFooter
        submitText="Save"
        cancelText="Cancel"
        isSubmitDisabled={!canSubmit}
        isSubmitting={isUpdatingFee}
        onCancel={() => onClose({ isSaving: false })}
        onSubmit={handleSaveClick}
        onCancelDataEventId={EVENT_IDS.CLICK_CANCEL_FEE_EDIT}
        onSubmitDataEventId={EVENT_IDS.CLICK_SAVE_FEE}
      />
    );
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose({ isSaving: false })}
      header={<Heading level={2}>Edit fee name and description</Heading>}
      body={
        <div data-testid="MultifamilyFeedImportedFeeModal">
          <Text>
            Create a custom fee name and/or description to show on your listing here. To change any other details for
            this fee, make edits directly in your feed.
          </Text>

          {apiError && (
            <Box css={{ paddingTop: '400' }}>
              <Alert tone="critical">{apiError}</Alert>
            </Box>
          )}

          <Box css={{ paddingTop: '400' }}>
            <Alert tone="info">
              If you have more than one fee by this name, your updates will be applied to all versions
            </Alert>
          </Box>

          {feeToEdit?.isCustomFeedFeeName && originalFeeName && (
            <Box css={{ marginTop: '400' }}>
              <FormField
                label={<Label>Original fee name</Label>}
                control={<Input value={originalFeeName} readOnly={true} />}
              />
            </Box>
          )}

          <Box css={{ marginTop: '400' }}>
            <FormField
              label={<Label optional={true}>Custom fee name</Label>}
              control={<Input value={feeDisplayName} onChange={handleFeeDisplayNameChange} maxLength={1000} />}
              error={Boolean(fieldErrors.customFeeName)}
              description={
                <FormHelp>
                  {fieldErrors.customFeeName ||
                    'This name will show on your listing. Clear this field if you want to show the original fee name.'}
                </FormHelp>
              }
            />
          </Box>

          <Box css={{ marginTop: '400' }}>
            <FormField
              label={<Label optional={true}>Description</Label>}
              control={
                <Input value={feeDisplayDescription} onChange={handleFeeDisplayDescriptionChange} maxLength={1023} />
              }
              description={<FormHelp>Only add a description if necessary, otherwise leave this blank</FormHelp>}
            />
          </Box>
        </div>
      }
      footer={renderFooter()}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default MultifamilyFeedImportedFeeModal;
